import {
  useState,
  createContext,
  useContext,
  useCallback,
  ReactNode,
} from 'react';
import { UseFormMethods } from 'react-hook-form';

import { AddressFormValues } from 'ui/forms/CheckoutAddressForm';

/**
 * Type for form field values stored in context
 */
type FormFieldValues = {
  [key: string]: string | boolean;
};

// Using AddressFormValues imported from CheckoutAddressForm

/**
 * Context type for checkout form data
 */
export type CheckoutFormContextType = {
  data: FormFieldValues;
  setPersistedFormField: (fieldName: string, value: string | boolean) => void;
  clearPersistedFormField: (fieldName: string) => void;
  clearAddressFormFields: (
    addressType: 'shippingAddress' | 'billingAddress'
  ) => void;
};

/**
 * Extended hook return type with form integration
 */
export type CheckoutFormHookReturnType = CheckoutFormContextType & {
  getPersistedFormField: (fieldName: string) => string | boolean;
};

const CheckoutFormContext = createContext<CheckoutFormContextType | null>(null);

interface CheckoutFormProviderProps {
  children: ReactNode;
}

export const CheckoutFormProvider: React.FC<CheckoutFormProviderProps> = ({
  children,
}) => {
  // Initialize with empty state
  const [data, setData] = useState<FormFieldValues>({});

  const setPersistedFormField = useCallback(
    (fieldName: string, value: string | boolean): void => {
      setData(prevData => ({
        ...prevData,
        [fieldName]: value,
      }));
    },
    []
  );

  const clearPersistedFormField = useCallback((fieldName: string): void => {
    setData(prevData => {
      const newData = { ...prevData };
      delete newData[fieldName];
      return newData;
    });
  }, []);

  const clearAddressFormFields = useCallback(
    (addressType: 'shippingAddress' | 'billingAddress'): void => {
      setData(prevData => {
        const newData = { ...prevData };
        Object.keys(newData).forEach(key => {
          if (key.startsWith(addressType)) {
            delete newData[key];
          }
        });
        return newData;
      });
    },
    []
  );

  return (
    <CheckoutFormContext.Provider
      value={{
        data,
        setPersistedFormField,
        clearPersistedFormField,
        clearAddressFormFields,
      }}
    >
      {children}
    </CheckoutFormContext.Provider>
  );
};

/**
 * Enhanced hook that provides form persistence utilities for checkout forms
 * using React Context API with optional form integration
 *
 * @param addressForm - Optional React Hook Form methods for form integration
 * @returns Form persistence utilities
 */
export const useCheckoutFormData = (
  addressForm?: UseFormMethods<AddressFormValues>
): CheckoutFormHookReturnType => {
  const context = useContext(CheckoutFormContext);

  if (!context) {
    throw new Error(
      'useCheckoutFormData must be used within a CheckoutFormProvider'
    );
  }

  const {
    data,
    setPersistedFormField,
    clearPersistedFormField,
    clearAddressFormFields,
  } = context;

  /**
   * Retrieves a form field value from context state
   *
   * @param fieldName - The full name of the field (e.g., 'shippingAddress.firstName')
   * @returns The stored value or empty string/false if not found
   */
  const getPersistedFormField = (fieldName: string): string | boolean => {
    if (typeof data[fieldName] === 'boolean') {
      return data[fieldName] || false;
    }
    return data[fieldName] || '';
  };

  /**
   * Enhanced version of setPersistedFormField that also updates form state if addressForm is provided
   */
  const enhancedSetPersistedFormField = (
    fieldName: string,
    value: string | boolean
  ): void => {
    // Call the original context method
    setPersistedFormField(fieldName, value);

    // Also update form state if addressForm is provided
    if (addressForm) {
      addressForm.setValue(fieldName, value);
    }
  };

  /**
   * Enhanced version of clearPersistedFormField that also updates form state if addressForm is provided
   */
  const enhancedClearPersistedFormField = (fieldName: string): void => {
    // Call the original context method
    clearPersistedFormField(fieldName);

    // Also clear from form state if addressForm is provided
    if (addressForm) {
      addressForm.setValue(fieldName, '');
      addressForm.clearErrors(fieldName);
    }
  };

  /**
   * Enhanced version of clearAddressFormFields that also updates form state if addressForm is provided
   */
  const enhancedClearAddressFormFields = (
    addressType: 'shippingAddress' | 'billingAddress'
  ): void => {
    try {
      // Call the original context method
      clearAddressFormFields(addressType);

      // Also clear from form state if addressForm is provided
      if (addressForm) {
        const formValues = addressForm.getValues();
        Object.keys(formValues[addressType] || {}).forEach(field => {
          const fieldName = `${addressType}.${field}`;
          addressForm.setValue(fieldName, '');
          addressForm.clearErrors(fieldName);
        });
      }
    } catch (error) {
      // Silent error handling
    }
  };

  return {
    data,
    setPersistedFormField: enhancedSetPersistedFormField,
    clearPersistedFormField: enhancedClearPersistedFormField,
    clearAddressFormFields: enhancedClearAddressFormFields,
    getPersistedFormField,
  };
};

export default useCheckoutFormData;
